import { AppText } from "@toothfairy/shared-ui";
import AppTheme from "@toothfairy/shared-ui/AppTheme";
import React from "react";
import { useTranslation } from "react-i18next";
import { View, TouchableOpacity, StyleSheet } from "react-native";
import ToothFairyButton from "../../../Components/ToothFairyComponents/ToothFairyButton";
import { isElectronRunning } from "../../../../App";
import { getWebAppUrl } from "../../../Utils/regionWebUrl";

// Brand mark colours + labels for the card networks Stripe reports. Rendered
// as a compact coloured pill rather than a third-party brand SVG so the UI
// stays self-contained and theme-consistent.
const BRAND_STYLES = {
  visa: { label: "VISA", bg: "#1A1F71" },
  mastercard: { label: "Mastercard", bg: "#EB001B" },
  amex: { label: "AMEX", bg: "#006FCF" },
  american_express: { label: "AMEX", bg: "#006FCF" },
  discover: { label: "Discover", bg: "#FF6000" },
  diners: { label: "Diners", bg: "#0079BE" },
  diners_club: { label: "Diners", bg: "#0079BE" },
  jcb: { label: "JCB", bg: "#0E4C96" },
  unionpay: { label: "UnionPay", bg: "#E21836" },
  unknown: { label: "Card", bg: "#6B7280" },
};

const getBrand = (brand) => {
  const key = String(brand || "").toLowerCase().replace(/[\s-]/g, "_");
  return BRAND_STYLES[key] || BRAND_STYLES.unknown;
};

// BillingPaymentMethod: a clean, compact list of all attached cards (the
// premium "saved payment methods" pattern). Each row shows the brand mark,
// masked number, expiry and a primary/backup status, with set-as-primary and
// remove actions. The primary is charged first by auto-recharge; the rest are
// failover backups.
const BillingPaymentMethod = ({
  handlePaymentPortalOpening,
  stripeCustomerData,
  paymentMethods = [],
  defaultPaymentMethodId,
  setPrimaryPaymentMethod,
  removePaymentMethod,
  paymentMethodAPIInProgress = false,
}) => {
  const { t } = useTranslation();
  const { currentTheme } = AppTheme.useTheme();
  const styles = generateStyles(currentTheme);
  const openTFonWeb = () => {
    window?.electronAPI?.openUrlOnWeb(getWebAppUrl("/profile/billing"));
  };

  const hasCards = paymentMethods.length > 0;
  const isLastCard = paymentMethods.length <= 1;

  const renderRow = (pm) => {
    const card = pm?.card || pm;
    const brand = getBrand(card?.brand);
    const isPrimary = pm.id === defaultPaymentMethodId;
    const expiry = `${String(card?.exp_month || "").padStart(2, "0")}/${String(
      card?.exp_year || "",
    ).slice(-2)}`;
    return (
      <View
        key={pm.id}
        style={[
          styles.row,
          isPrimary && styles.rowPrimary,
        ]}
      >
        {/* Brand mark */}
        <View style={[styles.brandMark, { backgroundColor: brand.bg }]}>
          <AppText
            preventTranslation
            style={styles.brandMarkText}
            numberOfLines={1}
          >
            {brand.label}
          </AppText>
        </View>

        {/* Masked number + expiry */}
        <View style={{ flex: 1, marginLeft: 10 }}>
          <AppText
            preventTranslation
            style={styles.cardNumber}
            numberOfLines={1}
          >
            •••• {card?.last4 || "••••"}
          </AppText>
          <AppText style={styles.cardExpiry}>
            {t("billing.expires", { defaultValue: "Expires" })} {expiry}
          </AppText>
        </View>

        {/* Status + actions */}
        <View style={styles.rowActions}>
          {isPrimary ? (
            <View style={styles.primaryBadge}>
              <AppText style={styles.primaryBadgeText}>
                {t("billing.paymentMethodPrimary")}
              </AppText>
            </View>
          ) : (
            <>
              <AppText style={styles.backupLabel}>
                {t("billing.paymentMethodBackup")}
              </AppText>
              <ToothFairyButton
                onPress={() => setPrimaryPaymentMethod?.(pm.id)}
                isDisabled={paymentMethodAPIInProgress}
                isSecondary
                paddingLeft={8}
                paddingRight={8}
                title={t("billing.setAsPrimary")}
              />
            </>
          )}
          {!isLastCard && (
            <TouchableOpacity
              onPress={() => removePaymentMethod?.(pm.id)}
              disabled={paymentMethodAPIInProgress}
              style={styles.removeBtn}
              accessibilityLabel={t("billing.removeCard")}
            >
              <AppText
                style={[
                  styles.removeText,
                  paymentMethodAPIInProgress && styles.removeDisabled,
                ]}
              >
                {t("billing.removeCard")}
              </AppText>
            </TouchableOpacity>
          )}
        </View>
      </View>
    );
  };

  return (
    <View>
      {!hasCards ? (
        <View style={styles.emptyState}>
          <AppText style={styles.emptyTitle}>
            {t("billing.noPaymentMethod")}
          </AppText>
          <AppText style={styles.emptyHint}>
            {t("billing.noPaymentMethodHint", {
              defaultValue:
                "Add a card to recharge credits and enable auto-recharge.",
            })}
          </AppText>
        </View>
      ) : (
        <View style={styles.list}>
          {paymentMethods.map(renderRow)}
        </View>
      )}

      <ToothFairyButton
        onPress={isElectronRunning() ? openTFonWeb : handlePaymentPortalOpening}
        paddingLeft={10}
        paddingRight={10}
        wrapperStyle={{
          marginHorizontal: "auto",
          marginTop: 16,
        }}
        title={
          isElectronRunning()
            ? t("billing.editOnBrowser")
            : hasCards
              ? t("billing.addPaymentMethod")
              : t("billing.editPaymentMethod")
        }
      />
      {hasCards && (
        <AppText style={styles.footerHint}>
          {t("billing.backupPaymentMethodsHint")}
        </AppText>
      )}
    </View>
  );
};

const generateStyles = (currentTheme) =>
  StyleSheet.create({
    list: {
      gap: 8,
    },
    row: {
      flexDirection: "row",
      alignItems: "center",
      padding: 12,
      borderRadius: 10,
      borderWidth: 1,
      borderColor: currentTheme?.theme?.border_color || "#E5E7EB",
      backgroundColor:
        currentTheme?.theme?.backgroundContrastColor ||
        currentTheme?.theme?.softBackgroundColor ||
        "transparent",
      flexWrap: "wrap",
    },
    rowPrimary: {
      borderColor: currentTheme?.theme?.primary || "#007AFF",
      borderWidth: 2,
    },
    brandMark: {
      minWidth: 52,
      height: 32,
      borderRadius: 6,
      alignItems: "center",
      justifyContent: "center",
      paddingHorizontal: 6,
    },
    brandMarkText: {
      color: "#FFFFFF",
      fontSize: 11,
      fontWeight: "bold",
    },
    cardNumber: {
      fontSize: 15,
      fontWeight: "bold",
      color: currentTheme?.theme?.font_color,
      letterSpacing: 1,
    },
    cardExpiry: {
      fontSize: 12,
      color: currentTheme?.theme?.secondary_font_color,
      marginTop: 2,
    },
    rowActions: {
      flexDirection: "row",
      alignItems: "center",
      gap: 8,
      flexWrap: "wrap",
      marginLeft: 8,
    },
    primaryBadge: {
      backgroundColor: currentTheme?.theme?.primary,
      borderRadius: 12,
      paddingHorizontal: 10,
      paddingVertical: 4,
    },
    primaryBadgeText: {
      color: currentTheme?.theme?.white || "#FFFFFF",
      fontSize: 12,
      fontWeight: "bold",
    },
    backupLabel: {
      fontSize: 12,
      color: currentTheme?.theme?.secondary_font_color,
    },
    removeBtn: {
      paddingHorizontal: 4,
    },
    removeText: {
      fontSize: 13,
      color: currentTheme?.theme?.orange,
    },
    removeDisabled: {
      opacity: 0.4,
    },
    emptyState: {
      padding: 16,
      alignItems: "center",
    },
    emptyTitle: {
      fontWeight: "bold",
      fontSize: 15,
      color: currentTheme?.theme?.font_color,
    },
    emptyHint: {
      fontSize: 13,
      color: currentTheme?.theme?.secondary_font_color,
      marginTop: 4,
      textAlign: "center",
    },
    footerHint: {
      fontSize: 12,
      color: currentTheme?.theme?.secondary_font_color,
      textAlign: "center",
      marginTop: 12,
    },
  });

export default BillingPaymentMethod;
